import React, { useState, useEffect, FC } from 'react';
import { withStyles } from '@material-ui/core/styles';
import InputBase from '@material-ui/core/InputBase';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import { Dialog, Tooltip, DialogTitle, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import CloudIcon from '@material-ui/icons/CloudOutlined';
import CloudOffIcon from '@material-ui/icons/CloudOffOutlined';
import CircularProgress from '@material-ui/core/CircularProgress';
import WordCloudChart from '../charts/WordCloudChart';
import {
  IsFilterActive,
  UpdateFilter,
  AddFilter,
  FilterTags,
  filters,
} from '../../signals/filterService';
import AdvancedSearchHelp from './AdvancedSearchHelp';
import { ADVANCED_SEARCH_PREFIX } from '../../constants';
import { createStyles, WithStyles } from '@material-ui/styles';
import { getReportWidgetData } from '../../services/WidgetDataService';
import { WidgetCode } from '../widgets/widgetCodes';
import { WordcloudDtoSchema } from '../../api/zodSchemas';
import { logDataFetchError, logResponseParseError } from '../../services/utils';
import CircularProgressCentral from '../common/CircularProgressCentral';

const styles = createStyles({
  root: {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    transition: 'all 0.25s',
    height: 44,
    background: 'transparent',
    boxShadow: 'unset',
  },
  input: {
    paddingLeft: 16,
    flex: 1,
    transition: 'all 0.2s',
    color: '#1a1a1a',
    height: '100%',
    '& input': {
      padding: 0,
    },
    border: '1px solid #d1d5db',
    borderRadius: 12,
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    '&:focus-within': {
      borderColor: 'rgba(223, 109, 46, 0.5)',
      boxShadow: '0 0 0 2px rgba(223, 109, 46, 0.12)',
    },
  },
  focused: {
    background: '#FFF',
    opacity: 1,
    color: '#1a1a1a !important',
  },
  iconButton: {
    width: 44,
    height: 44,
    border: '1px solid #d1d5db',
    borderRadius: 12,
    backgroundColor: '#ffffff',
    color: '#6b7280',
    flexShrink: 0,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#f9fafb',
      borderColor: 'rgba(223, 109, 46, 0.5)',
      color: 'var(--color-primary)',
    },
  },
  progress: {
    padding: 10,
    margin: 2,
  },
  inputWrapper: {
    position: 'relative' as const,
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    height: '100%',
  },
  clearButton: {
    position: 'absolute' as const,
    right: 4,
    zIndex: 1,
    padding: 6,
  },
});

interface Props extends WithStyles<typeof styles> {}

const SearchBox: FC<Props> = ({ classes }) => {
  const [searchText, setSearchText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusBar, setFocusBar] = useState(false);
  const [showWordcloud, setShowWordcloud] = useState(false);
  const [wordcloudLoading, setWordcloudLoading] = useState(true);
  const [wordcloud, setWordcloud] = useState<{ text: string; value: any }[]>([]);
  const [filtersChanged, setFiltersChanged] = useState(false);

  useEffect(() => {
    setFiltersChanged(true);
  }, [filters.value]);

  useEffect(() => {
    setWordcloudLoading(true);
    getReportWidgetData(WidgetCode.wordCloudData)
      .then((res) => {
        if (!res) return {};
        const schemaMatch = WordcloudDtoSchema.safeParse(res);
        if (schemaMatch.success) {
          setWordcloud(Object.keys(res).map((key) => ({ text: key, value: res[key] })));
        } else {
          //@ts-ignore
          logResponseParseError(schemaMatch.error);
        }
      })
      .catch((err) => logDataFetchError(err, 'Wordcloud'))
      .finally(() => {
        setWordcloudLoading(false);
        setFiltersChanged(false);
      });
  }, [filtersChanged]);

  const hasWordcloudData = wordcloud.length > 0;

  const textChangeHandler = (event) => {
    setSearchText(event.target.value);
  };

  const isAdvancedSearch = (searchText) => {
    const words = searchText ? searchText.split(' ') : [];
    return words.length > 1;
  };

  const submitHandler = () => {
    let currentSearchText = searchText && searchText.trim();

    if (
      isAdvancedSearch(currentSearchText) &&
      !currentSearchText.startsWith('Id:')
    ) {
      currentSearchText = `${ADVANCED_SEARCH_PREFIX}${currentSearchText}`;
    }

    // Filter object
    const filter = {
      name: currentSearchText,
      property: FilterTags.report.search,
      value: currentSearchText,
      count: 0,
    };

    if (IsFilterActive(filter)) {
      UpdateFilter(filter);
    } else {
      AddFilter(filter);
    }

    setSearchText('');
  };

  const keySubmitHandler = (e) => {
    if (e.keyCode === 13) {
      submitHandler();
    }
  };

  return (
    <>
      <Paper className={classes.root}>
        <div className={classes.inputWrapper}>
          <InputBase
            className={`${classes.input} ${focusBar ? classes.focused : ''}`}
            placeholder="Search"
            value={searchText}
            onChange={textChangeHandler}
            onKeyDown={keySubmitHandler}
            onMouseEnter={() => setFocusBar(true)}
            onMouseLeave={() => setFocusBar(false)}
          />
          {searchText !== '' && (
            <IconButton
              onClick={() => setSearchText('')}
              color="default"
              className={classes.clearButton}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </div>
        {isSubmitting ? (
          <CircularProgress size={30} className={classes.progress} />
        ) : (
          <>
            <IconButton onClick={submitHandler} className={classes.iconButton}>
              <SearchIcon />
            </IconButton>
            <AdvancedSearchHelp />
            <Tooltip title={hasWordcloudData ? 'Word Cloud' : 'No word cloud data'}>
              <span>
                <IconButton
                  className={classes.iconButton}
                  onClick={() => hasWordcloudData && setShowWordcloud(true)}
                  disabled={!hasWordcloudData}
                >
                  {hasWordcloudData ? <CloudIcon fontSize="small" /> : <CloudOffIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Paper>
      {showWordcloud && (
        <Dialog
          onClose={() => setShowWordcloud(false)}
          aria-labelledby="wordcloud"
          open={showWordcloud}
          className="wordcloud-dialog"
          PaperProps={{
            style: {
              borderRadius: 16,
              overflow: 'hidden',
              maxWidth: 'none',
            },
          }}
        >
          <DialogTitle
            disableTypography
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            <div>
              <Typography
                variant="h6"
                style={{ fontWeight: 600, fontSize: 16, color: '#1e293b' }}
              >
                Word Cloud
              </Typography>
              <Typography
                variant="body2"
                style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}
              >
                {wordcloud.length} keywords &middot; click a word to search
              </Typography>
            </div>
            <IconButton
              onClick={() => setShowWordcloud(false)}
              size="small"
              style={{ color: '#94a3b8' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          {wordcloudLoading ? (
            <div style={{ width: 800, height: 440, display: 'flex', placeContent: 'center' }}>
              <CircularProgressCentral />
            </div>
          ) : (
            <div style={{ width: 800, padding: '8px 16px 16px' }}>
              <WordCloudChart
                words={wordcloud}
                height={440}
                onWordClick={(word) => {
                  AddFilter({
                    name: word,
                    property: FilterTags.report.search,
                    value: word,
                    count: 0,
                  });
                  setShowWordcloud(false);
                }}
              />
            </div>
          )}
        </Dialog>
      )}
    </>
  );
};

export default withStyles(styles)(SearchBox);
